import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

// Calendly booking component
function CalendlyBooking({ calendlyUrl, companyName, onBookingComplete }) {
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [scriptError, setScriptError] = useState(false);

  useEffect(() => {
    // Check if Calendly script is already loaded
    if (window.Calendly) {
      setScriptLoaded(true);
      return;
    }

    // Check if script is already in the document
    const existingScript = document.querySelector('script[src="https://assets.calendly.com/assets/external/widget.js"]');
    if (existingScript) {
      // Script exists, wait for it to load
      const checkCalendly = setInterval(() => {
        if (window.Calendly) {
          setScriptLoaded(true);
          clearInterval(checkCalendly);
        }
      }, 100);

      return () => clearInterval(checkCalendly);
    }

    // Load Calendly widget script
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    
    script.onload = () => {
      setScriptLoaded(true);
    };
    
    script.onerror = () => {
      setScriptError(true);
    };

    document.body.appendChild(script);

    // Cleanup function - but don't remove if it might be used elsewhere
    return () => {
      // Only remove if we're sure it's safe
      if (script && script.parentNode && !document.querySelector('.calendly-inline-widget')) {
        try {
          document.body.removeChild(script);
        } catch (e) {
          // Script might already be removed, ignore error
        }
      }
    };
  }, []);

  useEffect(() => {
    // Initialize Calendly widget when script loads and URL is available
    if (scriptLoaded && window.Calendly) {
      // Validate the URL before passing to Calendly
      if (!calendlyUrl || typeof calendlyUrl !== 'string' || calendlyUrl.trim() === '') {
        setScriptError(true);
        return;
      }

      // Additional URL validation
      const trimmedUrl = calendlyUrl.trim();
      if (!trimmedUrl.startsWith('http://') && !trimmedUrl.startsWith('https://')) {
        setScriptError(true);
        return;
      }

      try {
        const element = document.getElementById('calendly-inline-widget');
        if (element) {
          // Clear any existing content
          element.innerHTML = '';
          
          window.Calendly.initInlineWidget({
            url: trimmedUrl,
            parentElement: element,
            prefill: {},
            utm: {}
          });
        } else {
          setScriptError(true);
        }
      } catch (error) {
        setScriptError(true);
      }
    }
  }, [scriptLoaded, calendlyUrl]);

  // Check if we have a valid URL
  const hasValidUrl = calendlyUrl && 
                     typeof calendlyUrl === 'string' && 
                     calendlyUrl.trim() !== '' &&
                     (calendlyUrl.trim().startsWith('http://') || calendlyUrl.trim().startsWith('https://'));

  if (scriptError || !hasValidUrl) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] px-8 py-12">
        <div className="max-w-md text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
            Book Your Interview
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-8 leading-relaxed">
            <a 
              className="font-medium text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300 transition-colors"
            >
              
            </a>{" "}
            to schedule your interview with{" "}
            <span className="font-semibold" style={{ color: "rgb(0, 185, 140)" }}>
              {companyName}
            </span>.
          </p>
          <button
            onClick={onBookingComplete}
            className="inline-flex items-center px-6 py-3 rounded-xl font-semibold text-white transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            style={{ 
              background: "linear-gradient(135deg, rgb(0, 242, 194) 0%, rgb(0, 185, 140) 100%)",
            }}
          >
            Continue
            <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  if (!scriptLoaded) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] px-8 py-12">
        <div className="text-center">
          <div className="relative w-16 h-16 mx-auto mb-6">
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 animate-pulse"></div>
            <div className="absolute inset-2 rounded-full bg-white dark:bg-gray-800"></div>
            <div className="absolute inset-4 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 animate-spin"></div>
          </div>
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
            Loading Calendar
          </h2>
          <p className="text-gray-600 dark:text-gray-300">
            Preparing your booking experience...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-5xl mx-auto">
      {/* Header Section */}
      <div className="text-center mb-6 px-6">
        <div className="inline-flex items-center justify-center w-12 h-12 mb-3 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500">
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Book your interview with{" "}
          <span className="font-semibold" style={{ color: "rgb(0, 185, 140)" }}>
            {companyName}
          </span>
        </h1>
        <p className="text-base text-gray-600 dark:text-gray-300">
          Select a convenient time for your interview
        </p>
      </div>

      {/* Calendly Widget Container */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden mb-4">
        <div 
          id="calendly-inline-widget" 
          style={{ minWidth: '320px', height: '500px' }}
          className="calendly-inline-widget"
        ></div>
      </div>

      {/* Footer Section */}
      <div className="text-center px-6">
        <div className="inline-flex flex-col sm:flex-row items-center gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
          <div className="flex-1 text-center sm:text-left">
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
              <strong>Next steps:</strong> You will have been introduced over email - please email the startup to confirm that you've booked your interview. If there are any issues or you can't make any of the available slots, simply respond to the introduction email.
            </p>

          </div>
          <button
            onClick={onBookingComplete}
            className="inline-flex items-center px-6 py-2.5 rounded-lg font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 transition-all duration-200 shadow-sm hover:shadow-md"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            I've completed my booking
          </button>
        </div>
      </div>
    </div>
  );
}

function InterviewRequest() {
  const query = useQuery();
  const interviewRequestId = query.get("interviewRequestId");
  const securityCode = query.get("securityCode");

  const navigate = useNavigate();

  const [active, setActive] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [interview, setInterview] = useState(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showCalendlyBooking, setShowCalendlyBooking] = useState(false);
  const [otherReason, setOtherReason] = useState("");
  const [selectedFeedback, setSelectedFeedback] = useState("");

  const feedbackOptions = [
    { label: "Salary is too low/high", value: "Salary mismatch" },
    { label: "The location or working style doesn't suit me", value: "Wrong location or working style" },
    { label: "I'm not interested in this industry", value: "Industry mismatch" },
    { label: "The role does not suit my skillset", value: "Missing skills" },
    { label: "I don't have time for new interviews", value: "Too many interviews" },
    { label: "I don't have enough information about the role", value: "Missing role info" },
    { label: "Other reason", value: "Other" }
  ];

  useEffect(() => {
    const fetchInterview = async () => {
      try {
        const response = await axios.get(
          `/api/interviews/fetchInterview?id=${interviewRequestId}`
        );

        const interview = response.data.interview;

        const transformedInterview = {
          company: Array.isArray(interview.fields["Name (from Startup) (from Role)"]) 
            ? interview.fields["Name (from Startup) (from Role)"][0] 
            : interview.fields["Name (from Startup) (from Role)"],
          calendlyLink: (() => {
            const hlCalendlyLink = interview.fields["HL Calendly Link (if applicable)"];
            const hiringLeadCalendlyLink = interview.fields["Hiring Lead Calendly Link"];
            
            // Handle array or string for HL Calendly Link
            let calendlyUrl = null;
            if (hlCalendlyLink) {
              calendlyUrl = Array.isArray(hlCalendlyLink) ? hlCalendlyLink[0] : hlCalendlyLink;
            } else if (hiringLeadCalendlyLink) {
              calendlyUrl = Array.isArray(hiringLeadCalendlyLink) ? hiringLeadCalendlyLink[0] : hiringLeadCalendlyLink;
            }
            
            return calendlyUrl;
          })(),
        };

        setInterview(transformedInterview);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchInterview();
  }, [interviewRequestId]);

  const handleSubmit = async (stage, candidateInterviewStage, feedback = null) => {
    const updatedData = {
      fields: {
        Stage: stage,
        "Candidate Interview Stage": candidateInterviewStage,
        ...(feedback && { "MR Rejection Feedback": feedback }),
      },
      interviewId: interviewRequestId,
    };
    setError(""); // Reset error message
    try {
      const response = await axios.patch(
        `/api/interviews/updateInterviewRequest`,
        updatedData
      );
      
      if (stage === "Interviewing") {
        // Check if we have a valid Calendly link
        const hasValidCalendlyLink = interview.calendlyLink && 
                                   typeof interview.calendlyLink === 'string' && 
                                   interview.calendlyLink.trim() !== '';
        
        if (hasValidCalendlyLink) {
          // Show Calendly booking if we have a valid link
          setShowCalendlyBooking(true);
        } else {
          // No valid Calendly link, just show thank you message like before
          setActive(false);
        }
      } else {
        setActive(false);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAccept = async () => {
    await handleSubmit("Interviewing", "Interviewing");
  };

  const handleReject = async () => {
    try {
      // First submit the rejection
      await handleSubmit("Rejected Interview Request", "Rejected Match Request");
      // Then show the feedback modal
      setShowFeedbackModal(true);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleFeedbackSubmit = async () => {
    const updatedData = {
      fields: {
        ...(selectedFeedback === "Other" 
          ? { "MR Rejection Feedback (other)": otherReason }
          : { "MR Rejection Feedback": selectedFeedback }
        ),
      },
      interviewId: interviewRequestId,
    };

    try {
      await axios.patch("/api/interviews/updateInterviewRequest", updatedData);
      setShowFeedbackModal(false);
      setActive(false);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleBookingComplete = () => {
    setShowCalendlyBooking(false);
    setActive(false);
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (showCalendlyBooking) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-4 px-4">
        <div className="w-full max-w-6xl mx-auto">
          <CalendlyBooking 
            calendlyUrl={interview.calendlyLink}
            companyName={interview.company}
            onBookingComplete={handleBookingComplete}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center px-6 py-8 mx-auto md:h-screen lg:py-0">
      <div className="w-full bg-white rounded-lg shadow dark:border md:mt-0 sm:max-w-lg xl:p-0 dark:bg-gray-800 dark:border-gray-700">
        <div className="p-6 space-y-4 md:space-y-6 sm:p-8">
          {active ? (
            <>
              <h1 className="text-xl font-normal leading-tight tracking-tight text-gray-900 md:text-2xl dark:text-white">
                You have been invited for an interview with{" "}
                <span
                  className="font-bold"
                  style={{
                    color: "rgb(0, 185, 140)",
                  }}
                >
                  {interview.company}
                </span>{" "}
                via Jumpstart!
              </h1>
              <div className="space-y-4 md:space-y-6">
                <button
                  onClick={handleAccept}
                  style={{
                    backgroundColor: "rgb(0, 242, 194)",
                  }}
                  className="w-full text-black font-medium rounded-lg text-lg px-5 py-2.5 text-center dark:bg-primary-600 dark:hover:bg-primary-700 dark:focus:ring-primary-800"
                >
                  Accept the interview request
                </button>
                <button
                  onClick={handleReject}
                  className="w-full text-white bg-red-500 font-medium rounded-lg text-lg px-5 py-2.5 text-center dark:bg-primary-600 dark:hover:bg-primary-700 dark:focus:ring-primary-800"
                >
                  Reject the interview request
                </button>
              </div>
              <h1>
                Please contact rachel@jumpstart-uk.com if you have any further
                questions!
              </h1>
            </>
          ) : (
            <>
              <h1 className="text-xl font-semibold leading-tight tracking-tight text-gray-900 md:text-2xl dark:text-white">
                Thank you for responding to this request.
              </h1>
              <span className="font-normal text-lg">
                We've let {interview.company} know about your response!
              </span>
            </>
          )}
        </div>
      </div>

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-semibold mb-4">Why are you rejecting this interview?</h2>
            <p className="text-sm text-gray-600 mb-4">NB: This is for Jumpstart only</p>
            <div className="space-y-3">
              {feedbackOptions.map((option) => (
                <div key={option.value} className="flex items-center">
                  <input
                    type="radio"
                    id={option.value}
                    name="feedback"
                    value={option.value}
                    checked={selectedFeedback === option.value}
                    onChange={(e) => setSelectedFeedback(e.target.value)}
                    className="mr-2"
                  />
                  <label htmlFor={option.value} className="text-sm">
                    {option.label}
                  </label>
                </div>
              ))}
              
              {selectedFeedback === "Other" && (
                <textarea
                  value={otherReason}
                  onChange={(e) => setOtherReason(e.target.value)}
                  placeholder="Please specify your reason..."
                  className="w-full p-2 border rounded-lg mt-2"
                  rows="3"
                />
              )}
            </div>
            
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowFeedbackModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleFeedbackSubmit}
                disabled={!selectedFeedback || (selectedFeedback === "Other" && !otherReason)}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default InterviewRequest;
